import React, { useState } from 'react'
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  Box,
  Chip,
  Typography,
  Drawer,
  Button,
  IconButton,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ClearIcon from '@mui/icons-material/Clear'
import { onFilterDropDownChange } from './helpers' // Ensure the import path is correct

export const MobileFilterDropdown = ({ filterDefinition, options, onChange, checked }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectOpen, setSelectOpen] = useState(false)

  checked = checked || []
  const theme = useTheme()
  let disabledProps = {}
  if (options.length === 0) {
    disabledProps = {
      disabled: true,
      sx: {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.action.disabled,
        },
        '& .MuiInputBase-input.Mui-disabled': {
          color: theme.palette.text.disabled,
        },
      },
    }
  }
  const clearFilter = () => {
    onChange({
      filterDefinition,
      selectedValues: [],
    })
  }
  const isMultiple = filterDefinition.isMultiple

  const toggleDrawer = open => () => {
    setIsOpen(open)
  }

  const handleSelectChange = event => {
    onFilterDropDownChange(event, options, filterDefinition, onChange)
  }

  const handleDelete = valueToRemove => event => {
    event.stopPropagation() // Ensure the event doesn't bubble up and open the dropdown
    const newChecked = checked.filter(item => item.name !== valueToRemove)
    onChange({
      filterDefinition,
      selectedValues: newChecked,
    })
  }

  const filtersSelectedCount = checked.length
  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <Button
        onClick={toggleDrawer(true)}
        variant='outlined'
        size='small'
        disabled={options.length === 0}
        sx={{
          width: '100%',
          maxWidth: '100%',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          textTransform: 'none',
          marginBottom: 0,
          borderRadius: '4px',
          borderColor: theme.palette.divider,
          justifyContent: 'flex-start',
          textAlign: 'left',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          '&:hover': {
            backgroundColor: 'white',
          },
        }}
      >
        {filterDefinition.displayName} {filtersSelectedCount > 0 ? `(${filtersSelectedCount})` : ''}
      </Button>
      <Drawer
        anchor='bottom'
        open={isOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            padding: 2,
            zIndex: 1200, // Lower z-index since no header to compete with
          },
        }}
        sx={{
          zIndex: 1200,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            cursor: 'pointer',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 2,
            paddingY: 1,
            backgroundColor: theme.palette.grey[50],
            borderRadius: '8px',
            paddingX: 2,
          }}
        >
          <Box>
            <Typography variant='h6' sx={{ color: theme.palette.text.primary }}>
              {filterDefinition.displayName}
            </Typography>
            <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
              Selections apply automatically
            </Typography>
          </Box>
          <IconButton 
            onClick={toggleDrawer(false)}
            sx={{
              width: '36px',
              height: '36px',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                backgroundColor: theme.palette.grey[100],
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ width: '100%' }}>
          <FormControl fullWidth {...disabledProps}>
            <InputLabel
              sx={{
                fontSize: '.9rem',
                color: theme.palette.text.primary,
                '&.MuiInputLabel-shrink': {
                  color: theme.palette.primary.main,
                },
              }}
              id={`label=${filterDefinition.name}`}
            >
              {filterDefinition.displayName}
            </InputLabel>
            <Select
              labelId={`label-${filterDefinition.name}`}
              id={filterDefinition.name}
              value={checked.map(item => item.name)}
              multiple={isMultiple}
              onChange={handleSelectChange}
              open={selectOpen}
              onOpen={() => setSelectOpen(true)}
              onClose={() => setSelectOpen(false)}
              displayEmpty
              defaultValue={[]}
              renderValue={selected =>
                isMultiple ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(value => (
                      <Chip
                        sx={{
                          height: '24px',
                          maxWidth: 'calc(50vw - 40px)', // Responsive max width based on viewport
                          minWidth: '60px',
                          '& .MuiChip-label': {
                            fontSize: '.8rem',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            maxWidth: 'calc(50vw - 80px)', // Account for delete icon
                            paddingLeft: '8px',
                            paddingRight: '4px',
                          },
                          '& .MuiChip-deleteIcon': {
                            fontSize: '16px',
                            color: theme.palette.text.secondary,
                            marginRight: '4px',
                            '&:hover': {
                              color: theme.palette.error.main,
                            },
                          },
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          margin: '2px',
                        }}
                        key={value}
                        label={value}
                        onDelete={handleDelete(value)}
                        deleteIcon={<ClearIcon sx={{ fontSize: '16px' }} />}
                        onMouseDown={e => e.stopPropagation()} // Prevent the dropdown from opening on delete icon click
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ 
                    color: theme.palette.text.primary, 
                    fontSize: '.8rem',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}>
                    {selected[0]}
                  </Typography>
                )
              }
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                width: '100%',
                maxWidth: '100%',
                '& .MuiSelect-select': {
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  paddingRight: '32px', // Account for dropdown arrow
                  paddingLeft: '12px',
                  maxHeight: '120px', // Allow more height on mobile
                  overflowY: 'auto', // Allow scrolling when content exceeds max height
                  overflowX: 'hidden', // Prevent horizontal overflow
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 0.25,
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  maxWidth: '100%',
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: '60vh',
                    width: '90vw',
                    maxWidth: '90vw',
                  },
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                sx: {
                  '& .MuiPaper-root': {
                    borderRadius: '12px 12px 0 0',
                    marginTop: '8px',
                  },
                  '& .MuiMenuItem-root': {
                    minHeight: '48px',
                    fontSize: '16px',
                  },
                },
              }}

            >
              {options.map(({ name, value }) => (
                <MenuItem
                  key={`${value}-${name}`}
                  value={name}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                >
                  <Checkbox checked={checked.some(item => item.name === name)} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
              {isMultiple && (
                <MenuItem
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    justifyContent: 'center',
                    borderTop: `1px solid ${theme.palette.divider}`,
                    marginTop: '8px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectOpen(false)
                      setIsOpen(false)
                    }}
                    sx={{
                      width: '80%',
                      minHeight: '44px',
                      fontSize: '16px',
                      fontWeight: 600,
                    }}
                  >
                    Done
                  </Button>
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
            <Button
              onClick={clearFilter}
              variant='outlined'
              color='secondary'
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              }}
            >
              Clear
            </Button>
          </Box>
        </Box>
      </Drawer>
    </div>
  )
}